import L from "leaflet";
import { TareaEjecucionResponse } from "../../../../modulos/rastro/response/TareaEjecucionResponse";
import { TareaEjecucionRecorridoResponse } from "../../../../modulos/rastro/response/TareaEjecucionRecorridoResponse";
import { fx_getTareaEjecucionRecorridosAsync } from "../../../../modulos/rastro/services/TareaEjecucionRecorridosGrpcService";
import { errorService } from "../../../../comun/services/ErrorService";
import { DiaTareaEjecucionesBucket } from "../../../../modulos/rastro/services/TareaEjecucionGetYearGroupListByFincaIdService";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import {
  colorDeEjecucion,
} from "../../../../utils/tareaEjecucionColorUtils";
import { BaseMapRender, PopupConfig } from "../../../base/map/BaseMapRender";
import { ConfigTopActions } from "../../../implementations/map/TopActions";

/** Capas Leaflet vivas de una ejecución (puntos + líneas), para poder
 *  quitarlas al ocultar/redibujar. */
type EjecucionLayers = {
  puntos: L.FeatureGroup;
  lineas: L.FeatureGroup;
};

/** Mapa del módulo de Tareas: lotes de la finca (base) + las ejecuciones de
 *  tarea del día seleccionado, sobreimpresas (puntos `ejecucion` + líneas
 *  `muestrasAutomaticas` de cada una, a la vez, en geometría cruda — el
 *  servidor ya no entrega tiles pre-renderizados). El sidebar controla qué se
 *  ve vía `setDia` (día completo) y `toggleEjecucion` (prende/apaga una
 *  puntual) — no hay selección por click en el mapa. El color de cada
 *  ejecución es determinístico (`colorDeEjecucion`), no viene del servidor.
 *  Desde el 2026-09-30 los recorridos no vienen en la lista: al elegir un día
 *  se piden con `fx_getTareaEjecucionRecorridosAsync`, que usa los guardados y
 *  baja solo los que faltan, con un aviso de carga mientras tanto. */
export class TareaEjecucionMapRender extends BaseMapRender<TareaEjecucionResponse> {
  constructor() {
    super();
    // El mapa Leaflet se crea async (connect): capturarlo y dibujar lo pendiente.
    this.setOnMapInitializeEnd((map) => {
      this._map = map;
      this._redraw();
    });
  }

  /** Muestra las ejecuciones del día (la llama el sidebar al seleccionar):
   *  limpia el mapa y pide sus recorridos. */
  setDia(dia: DiaTareaEjecucionesBucket): void {
    this._dia = dia;
    this._ocultas.clear();
    this._ultimaVisibleId = null;
    this._recorridos = new Map();
    this._redraw();
    void this._cargarRecorridosAsync(dia);
  }

  /** Prende/apaga una ejecución puntual sin afectar las demás del día. Al
   *  volver a mostrarla, queda por encima de las demás (`bringToFront`). */
  toggleEjecucion(ejecucionId: string, visible: boolean): void {
    if (visible) {
      this._ocultas.delete(ejecucionId);
      this._ultimaVisibleId = ejecucionId;
    } else {
      this._ocultas.add(ejecucionId);
    }
    this._redraw();
  }

  /** Sin datos extra: los lotes de la base bastan; las ejecuciones llegan por
   *  `setDia`/`toggleEjecucion`. */
  protected doInitData(): Promise<TareaEjecucionResponse[]> {
    return Promise.resolve([]);
  }

  /** Los lotes no tienen response asociada en este mapa. */
  protected doGetResponse(
    _lote: ClienteFincaLoteResponse,
    _responseList: TareaEjecucionResponse[],
  ): TareaEjecucionResponse | null {
    return null;
  }

  /** Limpieza: remueve todas las capas de ejecuciones dibujadas y descarta
   *  la carga de recorridos en curso. */
  protected doDispose(): void {
    this._pedido += 1;
    this._mostrarCargando(false);
    this._clearLayers();
  }

  /** Tooltip default; ortomosaico sí, conteo no (no aplica a este módulo). */
  protected getConfigTopActions(): ConfigTopActions<TareaEjecucionResponse> {
    return {
      toolTipItems: this.getDefaultTooltipItems(),
      withOrtomosaico: true,
      withConteo: false,
    };
  }

  /** Popup con los items default del lote; sin edición (no es el mapa de
   *  configuración de lotes). */
  protected getPopupConfig(): PopupConfig<TareaEjecucionResponse> | null {
    return {
      bindItems: (lote, _response) => this.getDefaultPopupItems(lote),
      withEditLote: false,
    };
  }

  /** Instancia Leaflet (disponible al terminar la inicialización). */
  private _map: L.Map | null = null;
  /** Día actualmente seleccionado (o null si aún no se seleccionó ninguno). */
  private _dia: DiaTareaEjecucionesBucket | null = null;
  /** Ids de ejecuciones ocultadas a mano (se resetea al cambiar de día). */
  private readonly _ocultas = new Set<string>();
  /** Capas vivas por ejecución, para poder quitarlas al redibujar. */
  private readonly _layers = new Map<string, EjecucionLayers>();
  /** Id de la última ejecución que se volvió a mostrar (toggle), para
   *  traerla al frente sobre las demás una vez dibujada. */
  private _ultimaVisibleId: string | null = null;
  /** Recorridos del día actual, por id de ejecución. */
  private _recorridos = new Map<string, TareaEjecucionRecorridoResponse>();
  /** Número del último pedido de recorridos: descarta respuestas de un día
   *  que ya no está seleccionado. */
  private _pedido = 0;
  /** Aviso "Cargando recorridos" sobre el mapa, mientras baja un día. */
  private _cargando: L.Control | null = null;

  /** Pide los recorridos del día y redibuja. Si el usuario cambió de día
   *  mientras tanto, la respuesta se descarta. */
  private async _cargarRecorridosAsync(dia: DiaTareaEjecucionesBucket): Promise<void> {
    const pedido = ++this._pedido;
    this._mostrarCargando(true);
    try {
      const recorridos = await fx_getTareaEjecucionRecorridosAsync(dia.ejecuciones);
      if (pedido !== this._pedido) return;
      this._recorridos = recorridos;
      this._redraw();
    } catch (error) {
      if (pedido !== this._pedido) return;
      const mensaje = error instanceof Error ? error.message : String(error);
      errorService.addError("No se pudieron cargar los recorridos del día", mensaje);
    } finally {
      if (pedido === this._pedido) this._mostrarCargando(false);
    }
  }

  /** Muestra u oculta el aviso de carga en la esquina del mapa. */
  private _mostrarCargando(visible: boolean): void {
    if (!visible) {
      this._cargando?.remove();
      this._cargando = null;
      return;
    }
    if (!this._map || this._cargando) return;
    const control = new L.Control({ position: "topright" });
    control.onAdd = () => {
      const aviso = L.DomUtil.create("div", "sx-rastro-cargando");
      aviso.textContent = "Cargando recorridos...";
      return aviso;
    };
    control.addTo(this._map);
    this._cargando = control;
  }

  /** Limpia todo y vuelve a dibujar las ejecuciones visibles del día actual.
   *  Trae al frente la última que se volvió a mostrar recién AL FINAL, cuando
   *  ya están agregadas todas las capas del redibujado — hacerlo capa por
   *  capa durante el loop no sirve: las que se agregan después igual quedan
   *  por encima. */
  private _redraw(): void {
    this._clearLayers();
    if (!this._map || !this._dia) return;

    for (const ejecucion of this._dia.ejecuciones) {
      if (this._ocultas.has(ejecucion.id)) continue;
      this._drawEjecucion(ejecucion);
    }

    if (this._ultimaVisibleId) {
      const layers = this._layers.get(this._ultimaVisibleId);
      layers?.puntos.bringToFront();
      layers?.lineas.bringToFront();
    }
  }

  /** Dibuja una ejecución (puntos + líneas) en geometría cruda, con su color
   *  determinístico dentro del día. */
  private _drawEjecucion(ejecucion: TareaEjecucionResponse): void {
    if (!this._map || !this._dia) return;
    // Sin recorrido todavía (cargando) o sin recorrido en el servidor: no se dibuja.
    const recorrido = this._recorridos.get(ejecucion.id);
    if (!recorrido) return;
    const color = colorDeEjecucion(this._dia.ejecuciones, ejecucion.id);

    const puntos = this._buildPuntosCrudos(recorrido, color);
    const lineas = this._buildLineasCrudas(recorrido, color);
    puntos.addTo(this._map);
    lineas.addTo(this._map);
    this._layers.set(ejecucion.id, { puntos, lineas });
  }

  /** Puntos crudos (`ejecucion`, GeoJSON MultiPoint) como círculos pequeños;
   *  vacío si la ejecución no tiene geometría de puntos. */
  private _buildPuntosCrudos(
    recorrido: TareaEjecucionRecorridoResponse,
    color: string,
  ): L.FeatureGroup {
    if (!recorrido.ejecucion) return L.featureGroup();
    return L.geoJSON(recorrido.ejecucion, {
      pointToLayer: (_feature, latlng) =>
        L.circleMarker(latlng, {
          radius: 3,
          color,
          fillColor: color,
          fillOpacity: 0.8,
          weight: 1,
        }),
    });
  }

  /** Líneas crudas (`muestrasAutomaticas`, GeoJSON MultiLineString). */
  private _buildLineasCrudas(
    recorrido: TareaEjecucionRecorridoResponse,
    color: string,
  ): L.FeatureGroup {
    return L.geoJSON(recorrido.muestrasAutomaticas, {
      style: { color, weight: 3, opacity: 0.8 },
    });
  }

  /** Remueve todas las capas dibujadas. */
  private _clearLayers(): void {
    this._layers.forEach(({ puntos, lineas }) => {
      puntos.remove();
      lineas.remove();
    });
    this._layers.clear();
  }
}
